import { supabase, isConfigured } from './supabase';
import type { AddressOption } from '../types';

/**
 * Look up all addresses on a given postcode.
 *
 * Two-tier strategy:
 *   1. Cache check — calls the `valmate_addresses_by_postcode`
 *      security-definer RPC (anon-callable, RLS-bypassing, ~10ms).
 *      Returns immediately if any properties are already cached for
 *      this postcode in the shared doorsteps_properties table.
 *
 *   2. Cache miss — calls the `valmate-fetch-postcode` edge function,
 *      which fetches sales for this postcode from the Land Registry
 *      Linked Data REST API, upserts them into doorsteps_properties
 *      and doorsteps_property_sales, and returns the freshly-cached
 *      addresses. ~500ms-1s for the first visitor; subsequent
 *      visitors hit the cache and get the fast path.
 *
 * This means we don't need an upfront bulk Land Registry ingestion
 * for Valmate to work UK-wide — coverage grows organically as real
 * users query postcodes. The bulk doorsteps-ingest-land-registry
 * monthly cron is still useful for keeping cached rows fresh, but
 * isn't a hard dependency.
 *
 * Returns an empty array only when Supabase isn't configured or the
 * postcode genuinely has no Land Registry sales on record (e.g. very
 * new builds, or postcodes that were entirely deleted from the LR
 * dataset).
 */
export async function findAddressesByPostcode(
  postcode: string
): Promise<AddressOption[]> {
  if (!isConfigured) return [];

  const normalised = postcode.trim().toUpperCase().replace(/\s+/g, ' ');
  if (!normalised) return [];

  // Step 1 — cache check.
  const cached = await rpcLookup(normalised);
  if (cached.length > 0) return cached;

  // Step 2 — lazy fetch from Land Registry, then return its result.
  // The edge function does the upsert itself, so the next visitor to
  // this same postcode hits the fast cache path.
  return await edgeFunctionFetch(normalised);
}

async function rpcLookup(postcode: string): Promise<AddressOption[]> {
  try {
    const { data, error } = await supabase.rpc('valmate_addresses_by_postcode', {
      p_postcode: postcode,
    });
    if (error) {
      console.warn('valmate_addresses_by_postcode error', error);
      return [];
    }
    return (data ?? []) as AddressOption[];
  } catch (err) {
    console.warn('valmate_addresses_by_postcode threw', err);
    return [];
  }
}

async function edgeFunctionFetch(postcode: string): Promise<AddressOption[]> {
  try {
    const { data, error } = await supabase.functions.invoke('valmate-fetch-postcode', {
      body: { postcode },
    });
    if (error) {
      console.warn('valmate-fetch-postcode error', error);
      return [];
    }
    if (!data?.success) {
      console.warn('valmate-fetch-postcode returned not-success', data);
      return [];
    }
    return (data.addresses ?? []) as AddressOption[];
  } catch (err) {
    console.warn('valmate-fetch-postcode threw', err);
    return [];
  }
}

/**
 * Format an address into a single human-readable line for a picker.
 * "12 Acacia Avenue, Chelmsford" or "Flat 4, 12 Acacia Avenue".
 */
export function formatAddress(a: AddressOption): string {
  return [a.address_line_1, a.address_line_2, a.town]
    .filter(Boolean)
    .join(', ');
}
